import React, { useState } from 'react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { TouchBackend } from 'react-dnd-touch-backend';
import { 
  Plus, 
  Calendar, 
  MapPin, 
  Flag, 
  Clock,
  User,
  Edit,
  Trash2,
  CheckCircle2,
  AlertCircle,
  Play,
  Pause,
  Menu,
  X,
  Filter
} from 'lucide-react';
import { useTheme } from '../../hooks/useTheme';

interface Subject {
  id: string;
  name: string;
  color: string;
}

interface Task {
  id: string;
  title: string;
  description: string;
  subjectId: string;
  dueDate: string;
  deliveryLocation: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'pending' | 'in-progress' | 'completed';
  createdAt: Date;
  estimatedHours?: number;
  tags: string[];
}

interface TaskCardProps {
  task: Task;
  subject: Subject;
  onUpdate: (task: Task) => void;
  onDelete: (id: string) => void;
  index: number;
  onMove: (dragIndex: number, hoverIndex: number, status: string) => void;
  isMobile?: boolean;
}

const TaskCard: React.FC<TaskCardProps> = ({ task, subject, onUpdate, onDelete, index, onMove, isMobile = false }) => {
  const { colors } = useTheme();
  const [isEditing, setIsEditing] = useState(false);
  const [showActions, setShowActions] = useState(false);

  const [{ isDragging }, drag] = useDrag({
    type: 'task',
    item: { index, status: task.status },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    canDrag: !isMobile, // Disable drag on mobile
  });

  const [, drop] = useDrop({
    accept: 'task',
    hover: (item: { index: number; status: string }) => {
      if (!isMobile && item.status === task.status && item.index !== index) {
        onMove(item.index, index, task.status);
        item.index = index;
      }
    },
  });

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return colors.red;
      case 'high': return colors.peach;
      case 'medium': return colors.yellow;
      case 'low': return colors.green;
      default: return colors.blue;
    }
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'urgent': return <AlertCircle className="w-3 h-3 sm:w-4 sm:h-4" />;
      case 'high': return <Flag className="w-3 h-3 sm:w-4 sm:h-4" />;
      case 'medium': return <Clock className="w-3 h-3 sm:w-4 sm:h-4" />;
      case 'low': return <CheckCircle2 className="w-3 h-3 sm:w-4 sm:h-4" />;
      default: return <Flag className="w-3 h-3 sm:w-4 sm:h-4" />;
    }
  };

  const isOverdue = new Date(task.dueDate) < new Date() && task.status !== 'completed';
  const daysUntilDue = Math.ceil((new Date(task.dueDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));

  return (
    <div
      ref={isMobile ? undefined : (node) => drag(drop(node))}
      className={`p-3 sm:p-4 rounded-lg border transition-all ${
        isMobile ? 'cursor-pointer' : 'cursor-move'
      } ${isDragging ? 'opacity-50' : 'hover:shadow-lg'}`}
      style={{ 
        backgroundColor: colors.base,
        borderColor: colors.surface1,
        borderLeft: `4px solid ${subject.color}`
      }}
      onClick={() => isMobile && setShowActions(!showActions)}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <h4 className="font-medium mb-1 text-sm sm:text-base line-clamp-2" style={{ color: colors.text }}>
            {task.title}
          </h4>
          <div 
            className="text-xs px-2 py-1 rounded inline-block mb-2"
            style={{ 
              backgroundColor: subject.color + '20',
              color: subject.color
            }}
          >
            {subject.name}
          </div>
        </div>
        
        <div className={`flex items-center gap-1 ${isMobile && !showActions ? 'hidden' : ''}`}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsEditing(true);
            }}
            className="p-1 sm:p-2 rounded hover:opacity-80"
            style={{ color: colors.subtext0 }}
          >
            <Edit className="w-3 h-3 sm:w-4 sm:h-4" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete(task.id);
            }}
            className="p-1 sm:p-2 rounded hover:opacity-80"
            style={{ color: colors.red }}
          >
            <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
          </button>
        </div>
      </div>

      {task.description && (
        <p className="text-xs sm:text-sm mb-3 line-clamp-2" style={{ color: colors.subtext0 }}>
          {task.description}
        </p>
      )}

      <div className="space-y-2 mb-3">
        <div className="flex items-center gap-2 text-xs" style={{ color: colors.subtext0 }}>
          <Calendar className="w-3 h-3" />
          <span className={isOverdue ? 'text-red-500 font-medium' : ''}>
            {new Date(task.dueDate).toLocaleDateString()}
            {daysUntilDue >= 0 && daysUntilDue <= 7 && (
              <span className="ml-1">
                ({daysUntilDue === 0 ? 'Hoy' : `${daysUntilDue} días`})
              </span>
            )}
          </span>
        </div>

        {task.deliveryLocation && (
          <div className="flex items-center gap-2 text-xs" style={{ color: colors.subtext0 }}>
            <MapPin className="w-3 h-3" />
            <span className="truncate">{task.deliveryLocation}</span>
          </div>
        )}

        <div className="flex items-center gap-2 text-xs">
          <div 
            className="flex items-center gap-1 px-2 py-1 rounded"
            style={{ 
              backgroundColor: getPriorityColor(task.priority) + '20',
              color: getPriorityColor(task.priority)
            }}
          >
            {getPriorityIcon(task.priority)}
            <span className="hidden sm:inline">
              {task.priority.charAt(0).toUpperCase() + task.priority.slice(1)}
            </span>
          </div>
        </div>
      </div>

      {task.tags.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-3">
          {task.tags.slice(0, isMobile ? 2 : 3).map((tag) => (
            <span
              key={tag}
              className="text-xs px-2 py-1 rounded"
              style={{ 
                backgroundColor: colors.surface0,
                color: colors.text
              }}
            >
              {tag}
            </span>
          ))}
          {task.tags.length > (isMobile ? 2 : 3) && (
            <span
              className="text-xs px-2 py-1 rounded"
              style={{ 
                backgroundColor: colors.surface0,
                color: colors.subtext0
              }}
            >
              +{task.tags.length - (isMobile ? 2 : 3)}
            </span>
          )}
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="text-xs" style={{ color: colors.subtext0 }}>
          {task.estimatedHours && `${task.estimatedHours}h estimadas`}
        </div>
        
        <div className={`flex items-center gap-1 ${isMobile && !showActions ? 'hidden' : ''}`}>
          {task.status === 'pending' && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onUpdate({ ...task, status: 'in-progress' });
              }}
              className="p-1 rounded hover:opacity-80"
              style={{ color: colors.blue }}
              title="Iniciar tarea"
            >
              <Play className="w-3 h-3" />
            </button>
          )}
          
          {task.status === 'in-progress' && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onUpdate({ ...task, status: 'pending' });
                }}
                className="p-1 rounded hover:opacity-80"
                style={{ color: colors.yellow }}
                title="Pausar tarea"
              >
                <Pause className="w-3 h-3" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onUpdate({ ...task, status: 'completed' });
                }}
                className="p-1 rounded hover:opacity-80"
                style={{ color: colors.green }}
                title="Completar tarea"
              >
                <CheckCircle2 className="w-3 h-3" />
              </button>
            </>
          )}
          
          {task.status === 'completed' && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onUpdate({ ...task, status: 'in-progress' });
              }}
              className="p-1 rounded hover:opacity-80"
              style={{ color: colors.blue }}
              title="Reabrir tarea"
            >
              <Play className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>

      {isMobile && showActions && (
        <div className="mt-3 pt-3 border-t flex justify-center" style={{ borderColor: colors.surface1 }}>
          <span className="text-xs" style={{ color: colors.subtext0 }}>
            Toca para ocultar acciones
          </span>
        </div>
      )}
    </div>
  );
};

interface ColumnProps {
  title: string;
  status: string;
  tasks: Task[];
  subjects: Subject[];
  onTaskUpdate: (task: Task) => void;
  onTaskDelete: (id: string) => void;
  onTaskMove: (dragIndex: number, hoverIndex: number, status: string) => void;
  onStatusChange: (taskId: string, newStatus: string) => void;
  color: string;
  isMobile?: boolean;
}

const Column: React.FC<ColumnProps> = ({ 
  title, 
  status, 
  tasks, 
  subjects, 
  onTaskUpdate, 
  onTaskDelete, 
  onTaskMove,
  onStatusChange,
  color,
  isMobile = false
}) => {
  const { colors } = useTheme();

  const [, drop] = useDrop({
    accept: 'task',
    drop: (item: { index: number; status: string }) => {
      if (!isMobile && item.status !== status) {
        const draggedTask = tasks.find(t => t.status === item.status);
        if (draggedTask) {
          onStatusChange(draggedTask.id, status);
        }
      }
    },
  });

  return (
    <div 
      ref={isMobile ? undefined : drop}
      className={`${isMobile ? 'w-full' : 'flex-1 min-w-[300px]'} min-h-[400px] sm:min-h-[600px]`}
    >
      <div 
        className="p-3 sm:p-4 rounded-t-lg border-b-2"
        style={{ 
          backgroundColor: colors.mantle,
          borderColor: color
        }}
      >
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-sm sm:text-base" style={{ color: colors.text }}>
            {title}
          </h3>
          <span 
            className="text-xs sm:text-sm px-2 py-1 rounded"
            style={{ 
              backgroundColor: colors.surface0,
              color: colors.text
            }}
          >
            {tasks.length}
          </span>
        </div>
      </div>
      
      <div 
        className="p-3 sm:p-4 space-y-3 min-h-[300px] sm:min-h-[500px]"
        style={{ backgroundColor: colors.mantle }}
      >
        {tasks.map((task, index) => {
          const subject = subjects.find(s => s.id === task.subjectId);
          if (!subject) return null;
          
          return (
            <TaskCard
              key={task.id}
              task={task}
              subject={subject}
              onUpdate={onTaskUpdate}
              onDelete={onTaskDelete}
              index={index}
              onMove={onTaskMove}
              isMobile={isMobile}
            />
          );
        })}
        
        {tasks.length === 0 && (
          <div className="text-center py-8">
            <div 
              className="w-12 h-12 sm:w-16 sm:h-16 rounded-full mx-auto mb-3 flex items-center justify-center"
              style={{ backgroundColor: colors.surface0 }}
            >
              <CheckCircle2 className="w-6 h-6 sm:w-8 sm:h-8" style={{ color: colors.subtext0 }} />
            </div>
            <p className="text-sm" style={{ color: colors.subtext0 }}>
              No hay tareas en esta columna
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

const TaskBoard: React.FC = () => {
  const { colors } = useTheme();
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [filter, setFilter] = useState<string>('all');
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [viewMode, setViewMode] = useState<'board' | 'list'>('board');
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile
  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const subjects: Subject[] = [
    { id: '1', name: 'Matemáticas Avanzadas', color: colors.blue },
    { id: '2', name: 'Programación Web', color: colors.green },
    { id: '3', name: 'Base de Datos', color: colors.purple },
    { id: '4', name: 'Diseño UI/UX', color: colors.pink }
  ];

  const [tasks, setTasks] = useState<Task[]>([
    {
      id: '1',
      title: 'Proyecto Final React',
      description: 'Desarrollar aplicación web completa con React y Node.js',
      subjectId: '2',
      dueDate: '2025-02-15',
      deliveryLocation: 'Plataforma Virtual',
      priority: 'high',
      status: 'in-progress',
      createdAt: new Date('2025-01-10'),
      estimatedHours: 40,
      tags: ['React', 'Node.js', 'Proyecto']
    },
    {
      id: '2',
      title: 'Examen Parcial',
      description: 'Examen sobre cálculo diferencial e integral',
      subjectId: '1',
      dueDate: '2025-01-25',
      deliveryLocation: 'Aula A-101',
      priority: 'urgent',
      status: 'pending',
      createdAt: new Date('2025-01-15'),
      estimatedHours: 8,
      tags: ['Examen', 'Cálculo']
    },
    {
      id: '3',
      title: 'Diseño de BD',
      description: 'Crear modelo entidad-relación para sistema de biblioteca',
      subjectId: '3',
      dueDate: '2025-02-01',
      deliveryLocation: 'Oficina Profesora',
      priority: 'medium',
      status: 'pending',
      createdAt: new Date('2025-01-12'),
      estimatedHours: 12,
      tags: ['Modelado', 'ER']
    },
    {
      id: '4',
      title: 'Prototipo App Móvil',
      description: 'Crear prototipo interactivo en Figma',
      subjectId: '4',
      dueDate: '2025-01-30',
      deliveryLocation: 'Presentación Online',
      priority: 'high',
      status: 'completed',
      createdAt: new Date('2025-01-08'),
      estimatedHours: 16,
      tags: ['Figma', 'Prototipo', 'Móvil']
    }
  ]);

  const [newTask, setNewTask] = useState<Partial<Task>>({
    title: '',
    description: '',
    subjectId: '',
    dueDate: '',
    deliveryLocation: '',
    priority: 'medium',
    status: 'pending',
    estimatedHours: 0,
    tags: []
  });

  const handleTaskUpdate = (updatedTask: Task) => {
    setTasks(prev => prev.map(task => 
      task.id === updatedTask.id ? updatedTask : task
    ));
  };

  const handleTaskDelete = (taskId: string) => {
    if (confirm('¿Estás seguro de que quieres eliminar esta tarea?')) {
      setTasks(prev => prev.filter(task => task.id !== taskId));
    }
  };

  const handleTaskMove = (dragIndex: number, hoverIndex: number, status: string) => {
    const statusTasks = getTasksByStatus(status);
    const dragTask = statusTasks[dragIndex];
    const newTasks = [...statusTasks];
    newTasks.splice(dragIndex, 1);
    newTasks.splice(hoverIndex, 0, dragTask);
    
    // Update the full tasks array
    setTasks(prev => {
      const otherTasks = prev.filter(task => task.status !== status);
      return [...otherTasks, ...newTasks];
    });
  };

  const handleStatusChange = (taskId: string, newStatus: string) => {
    setTasks(prev => prev.map(task => 
      task.id === taskId ? { ...task, status: newStatus as any } : task
    ));
  };

  const handleSaveTask = () => {
    if (newTask.title && newTask.subjectId && newTask.dueDate) {
      const task: Task = {
        id: Date.now().toString(),
        title: newTask.title,
        description: newTask.description || '',
        subjectId: newTask.subjectId,
        dueDate: newTask.dueDate,
        deliveryLocation: newTask.deliveryLocation || '',
        priority: newTask.priority || 'medium',
        status: newTask.status || 'pending',
        createdAt: new Date(),
        estimatedHours: newTask.estimatedHours || 0,
        tags: newTask.tags || []
      };
      
      setTasks(prev => [...prev, task]);
      setNewTask({
        title: '',
        description: '',
        subjectId: '',
        dueDate: '',
        deliveryLocation: '',
        priority: 'medium',
        status: 'pending',
        estimatedHours: 0,
        tags: []
      });
      setShowTaskModal(false);
    }
  };

  const getTasksByStatus = (status: string) => {
    let filteredTasks = tasks.filter(task => task.status === status);
    
    if (filter !== 'all') {
      filteredTasks = filteredTasks.filter(task => task.subjectId === filter);
    }
    
    return filteredTasks;
  };

  const columns = [
    { title: 'Pendientes', status: 'pending', color: colors.yellow },
    { title: 'En Progreso', status: 'in-progress', color: colors.blue },
    { title: 'Completadas', status: 'completed', color: colors.green }
  ];

  const getTaskStats = () => {
    const total = tasks.length;
    const completed = tasks.filter(t => t.status === 'completed').length;
    const inProgress = tasks.filter(t => t.status === 'in-progress').length;
    const pending = tasks.filter(t => t.status === 'pending').length;
    const overdue = tasks.filter(t => 
      new Date(t.dueDate) < new Date() && t.status !== 'completed'
    ).length;
    
    return { total, completed, inProgress, pending, overdue };
  };

  const stats = getTaskStats();

  // Use touch backend for mobile
  const backend = isMobile ? TouchBackend : HTML5Backend;

  return (
    <DndProvider backend={backend}>
      <div className="space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <h1 className="text-2xl sm:text-3xl font-bold" style={{ color: colors.text }}>
            Tablero de Tareas
          </h1>
          
          <div className="flex items-center gap-2">
            {isMobile && (
              <div 
                className="flex items-center rounded-lg border"
                style={{ 
                  backgroundColor: colors.mantle,
                  borderColor: colors.surface0
                }}
              >
                <button
                  onClick={() => setViewMode('board')}
                  className={`px-2 py-2 rounded-l-lg transition-colors text-xs ${
                    viewMode === 'board' ? 'text-white' : ''
                  }`}
                  style={{
                    backgroundColor: viewMode === 'board' ? colors.blue : 'transparent',
                    color: viewMode === 'board' ? 'white' : colors.text
                  }}
                >
                  Tablero
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`px-2 py-2 rounded-r-lg transition-colors text-xs ${
                    viewMode === 'list' ? 'text-white' : ''
                  }`}
                  style={{
                    backgroundColor: viewMode === 'list' ? colors.blue : 'transparent',
                    color: viewMode === 'list' ? 'white' : colors.text
                  }}
                >
                  Lista
                </button>
              </div>
            )}
            
            <button 
              onClick={() => setShowTaskModal(true)}
              className="px-3 sm:px-4 py-2 rounded-lg font-medium text-white transition-all hover:opacity-90 flex items-center gap-2"
              style={{ backgroundColor: colors.blue }}
            >
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">Nueva Tarea</span>
              <span className="sm:hidden">Nueva</span>
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 sm:gap-4">
          <div 
            className="p-3 sm:p-4 rounded-lg text-center"
            style={{ backgroundColor: colors.mantle }}
          >
            <div className="text-xl sm:text-2xl font-bold" style={{ color: colors.text }}>
              {stats.total}
            </div>
            <div className="text-xs sm:text-sm" style={{ color: colors.subtext0 }}>
              Total
            </div>
          </div>
          
          <div 
            className="p-3 sm:p-4 rounded-lg text-center"
            style={{ backgroundColor: colors.mantle }}
          >
            <div className="text-xl sm:text-2xl font-bold" style={{ color: colors.yellow }}>
              {stats.pending}
            </div>
            <div className="text-xs sm:text-sm" style={{ color: colors.subtext0 }}>
              Pendientes
            </div>
          </div>
          
          <div 
            className="p-3 sm:p-4 rounded-lg text-center"
            style={{ backgroundColor: colors.mantle }}
          >
            <div className="text-xl sm:text-2xl font-bold" style={{ color: colors.blue }}>
              {stats.inProgress}
            </div>
            <div className="text-xs sm:text-sm" style={{ color: colors.subtext0 }}>
              En Progreso
            </div>
          </div>
          
          <div 
            className="p-3 sm:p-4 rounded-lg text-center"
            style={{ backgroundColor: colors.mantle }}
          >
            <div className="text-xl sm:text-2xl font-bold" style={{ color: colors.green }}>
              {stats.completed}
            </div>
            <div className="text-xs sm:text-sm" style={{ color: colors.subtext0 }}>
              Completadas
            </div>
          </div>
          
          <div 
            className="p-3 sm:p-4 rounded-lg text-center col-span-2 sm:col-span-1"
            style={{ backgroundColor: colors.mantle }}
          >
            <div className="text-xl sm:text-2xl font-bold" style={{ color: colors.red }}>
              {stats.overdue}
            </div>
            <div className="text-xs sm:text-sm" style={{ color: colors.subtext0 }}>
              Vencidas
            </div>
          </div>
        </div>

        {/* Mobile Filters Toggle */}
        {isMobile && (
          <button
            onClick={() => setShowMobileFilters(!showMobileFilters)}
            className="w-full p-3 rounded-lg border transition-colors hover:opacity-80 flex items-center justify-between"
            style={{ 
              backgroundColor: colors.mantle,
              borderColor: colors.surface0,
              color: colors.text
            }}
          >
            <span>Filtros</span>
            <Menu className="w-4 h-4" />
          </button>
        )}

        {/* Filters */}
        <div className={`${showMobileFilters || !isMobile ? 'block' : 'hidden'}`}>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
            <span className="text-sm font-medium" style={{ color: colors.text }}>
              Filtrar por materia:
            </span>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="px-3 py-2 rounded border"
              style={{
                backgroundColor: colors.mantle,
                borderColor: colors.surface0,
                color: colors.text
              }}
            >
              <option value="all">Todas las materias</option>
              {subjects.map((subject) => (
                <option key={subject.id} value={subject.id}>
                  {subject.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Kanban Board / List View */}
        {viewMode === 'board' ? (
          <div className={`${isMobile ? 'space-y-6' : 'flex gap-6 overflow-x-auto'}`}>
            {columns.map((column) => (
              <Column
                key={column.status}
                title={column.title}
                status={column.status}
                tasks={getTasksByStatus(column.status)}
                subjects={subjects}
                onTaskUpdate={handleTaskUpdate}
                onTaskDelete={handleTaskDelete}
                onTaskMove={handleTaskMove}
                onStatusChange={handleStatusChange}
                color={column.color}
                isMobile={isMobile}
              />
            ))}
          </div>
        ) : (
          // List view for mobile
          <div className="space-y-4">
            {tasks.map((task) => {
              const subject = subjects.find(s => s.id === task.subjectId);
              if (!subject) return null;
              
              return (
                <TaskCard
                  key={task.id}
                  task={task}
                  subject={subject}
                  onUpdate={handleTaskUpdate}
                  onDelete={handleTaskDelete}
                  index={0}
                  onMove={() => {}}
                  isMobile={true}
                />
              );
            })}
          </div>
        )}

        {/* Task Modal */}
        {showTaskModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div 
              className="bg-white p-4 sm:p-6 rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
              style={{ backgroundColor: colors.mantle }}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold" style={{ color: colors.text }}>
                  Nueva Tarea
                </h3>
                <button
                  onClick={() => setShowTaskModal(false)}
                  className="p-1 rounded hover:opacity-80"
                  style={{ color: colors.subtext0 }}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-4">
                <input
                  type="text"
                  placeholder="Título de la tarea"
                  value={newTask.title || ''}
                  onChange={(e) => setNewTask(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-3 py-2 rounded border"
                  style={{
                    backgroundColor: colors.base,
                    borderColor: colors.surface1,
                    color: colors.text
                  }}
                />
                
                <textarea
                  placeholder="Descripción"
                  value={newTask.description || ''}
                  onChange={(e) => setNewTask(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 rounded border resize-none"
                  rows={3}
                  style={{
                    backgroundColor: colors.base,
                    borderColor: colors.surface1,
                    color: colors.text
                  }}
                />
                
                <select
                  value={newTask.subjectId || ''}
                  onChange={(e) => setNewTask(prev => ({ ...prev, subjectId: e.target.value }))}
                  className="w-full px-3 py-2 rounded border"
                  style={{
                    backgroundColor: colors.base,
                    borderColor: colors.surface1,
                    color: colors.text
                  }}
                >
                  <option value="">Seleccionar materia</option>
                  {subjects.map((subject) => (
                    <option key={subject.id} value={subject.id}>
                      {subject.name}
                    </option>
                  ))}
                </select>
                
                <input
                  type="date"
                  value={newTask.dueDate || ''}
                  onChange={(e) => setNewTask(prev => ({ ...prev, dueDate: e.target.value }))}
                  className="w-full px-3 py-2 rounded border"
                  style={{
                    backgroundColor: colors.base,
                    borderColor: colors.surface1,
                    color: colors.text
                  }}
                />
                
                <input
                  type="text"
                  placeholder="Lugar de entrega"
                  value={newTask.deliveryLocation || ''}
                  onChange={(e) => setNewTask(prev => ({ ...prev, deliveryLocation: e.target.value }))}
                  className="w-full px-3 py-2 rounded border"
                  style={{
                    backgroundColor: colors.base,
                    borderColor: colors.surface1,
                    color: colors.text
                  }}
                />
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <select
                    value={newTask.priority || 'medium'}
                    onChange={(e) => setNewTask(prev => ({ ...prev, priority: e.target.value as any }))}
                    className="px-3 py-2 rounded border"
                    style={{
                      backgroundColor: colors.base,
                      borderColor: colors.surface1,
                      color: colors.text
                    }}
                  >
                    <option value="low">Baja</option>
                    <option value="medium">Media</option>
                    <option value="high">Alta</option>
                    <option value="urgent">Urgente</option>
                  </select>
                  
                  <input
                    type="number"
                    placeholder="Horas estimadas"
                    value={newTask.estimatedHours || ''}
                    onChange={(e) => setNewTask(prev => ({ ...prev, estimatedHours: parseInt(e.target.value) || 0 }))}
                    className="px-3 py-2 rounded border"
                    style={{
                      backgroundColor: colors.base,
                      borderColor: colors.surface1,
                      color: colors.text
                    }}
                  />
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row items-center gap-2 mt-6">
                <button
                  onClick={handleSaveTask}
                  className="w-full sm:flex-1 py-2 rounded font-medium text-white"
                  style={{ backgroundColor: colors.blue }}
                >
                  Guardar
                </button>
                <button
                  onClick={() => setShowTaskModal(false)}
                  className="w-full sm:flex-1 py-2 rounded border"
                  style={{ 
                    backgroundColor: colors.base,
                    borderColor: colors.surface1,
                    color: colors.text
                  }}
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DndProvider>
  );
};

export default TaskBoard;